import { CLIENT_BUILD } from '../net/build-check';

export const PHASE_NAMES = [
  'frame',
  'net',
  'tick',
  'send',
  'interpolate',
  'camera',
  'resize',
  'effectsAdvance',
  'clear',
  'starfield',
  'starfieldFar',
  'starfieldNear',
  'nebulae',
  'trailBlit',
  'mapAndBases',
  'scrap',
  'statics',
  'dronesUnder',
  'players',
  'dronesOver',
  'effectsWorld',
  'emojiBar',
  'hud',
] as const;

export type PhaseName = (typeof PHASE_NAMES)[number];

export const PHASE = Object.fromEntries(PHASE_NAMES.map((name, i) => [name, i])) as Record<
  PhaseName,
  number
>;

export const COUNTER_NAMES = [
  'entities',
  'players',
  'playersDrawn',
  'drones',
  'scrap',
  'turrets',
  'controlPoints',
  'otherEntities',
  'explosions',
  'lasers',
  'damageNumbers',
  'emoji',
  'pendingBombs',
  'ticks',
] as const;

export type CounterName = (typeof COUNTER_NAMES)[number];

export const COUNTER = Object.fromEntries(COUNTER_NAMES.map((name, i) => [name, i])) as Record<
  CounterName,
  number
>;

export interface Distribution {
  readonly n: number;
  readonly mean: number;
  readonly min: number;
  readonly p50: number;
  readonly p75: number;
  readonly p90: number;
  readonly p95: number;
  readonly p99: number;
  readonly max: number;
  readonly total: number;
}

export interface PhaseDistribution extends Distribution {
  readonly shareOfFrame: number;
}

export interface LoadBucket {
  readonly entitiesFrom: number;
  readonly entitiesTo: number | null;
  readonly frames: number;
  readonly frameMsP50: number;
  readonly frameMsP95: number;
  readonly frameMsMax: number;
}

export interface WorstFrame {
  readonly atMs: number;
  readonly frameMs: number;
  readonly gapMs: number;
  readonly phaseMs: Partial<Record<PhaseName, number>>;
  readonly counts: Partial<Record<CounterName, number>>;
}

export interface ProfileSnapshot {
  readonly format: 'wis-render-profile/1';
  readonly label: string;
  readonly build: string;
  readonly page: Record<string, string | number>;
  readonly frames: number;
  readonly capacity: number;
  readonly wrapped: boolean;
  readonly spanMs: number;
  readonly fps: number;
  readonly frameMs: Distribution;
  readonly gapMs: Distribution;
  readonly jank: Record<string, number>;
  readonly phases: Record<string, PhaseDistribution>;
  readonly counts: Record<string, Distribution>;
  readonly entityCorrelation: number;
  readonly byEntityCount: readonly LoadBucket[];
  readonly worstFrames: readonly WorstFrame[];
}

const DEFAULT_CAPACITY = 3600;
const LOAD_BOUNDS = [0, 25, 50, 100, 200, 400, 800];
const WORST_FRAMES = 8;

function r3(value: number): number {
  return Math.round(value * 1000) / 1000;
}

export function percentile(sorted: Float64Array, q: number): number {
  const n = sorted.length;
  if (n === 0) return 0;
  const rank = Math.ceil(q * n) - 1;
  const index = rank < 0 ? 0 : rank >= n ? n - 1 : rank;
  return sorted[index] ?? 0;
}

const EMPTY: Distribution = {
  n: 0,
  mean: 0,
  min: 0,
  p50: 0,
  p75: 0,
  p90: 0,
  p95: 0,
  p99: 0,
  max: 0,
  total: 0,
};

export function summarise(values: Float64Array): Distribution {
  const n = values.length;
  if (n === 0) return EMPTY;
  const sorted = Float64Array.from(values).sort();
  let total = 0;
  for (const value of values) total += value;
  return {
    n,
    mean: r3(total / n),
    min: r3(sorted[0] ?? 0),
    p50: r3(percentile(sorted, 0.5)),
    p75: r3(percentile(sorted, 0.75)),
    p90: r3(percentile(sorted, 0.9)),
    p95: r3(percentile(sorted, 0.95)),
    p99: r3(percentile(sorted, 0.99)),
    max: r3(sorted[n - 1] ?? 0),
    total: r3(total),
  };
}

export function correlation(xs: Float64Array, ys: Float64Array): number {
  const n = Math.min(xs.length, ys.length);
  if (n < 2) return 0;
  let sx = 0;
  let sy = 0;
  for (let i = 0; i < n; i++) {
    sx += xs[i] ?? 0;
    sy += ys[i] ?? 0;
  }
  const mx = sx / n;
  const my = sy / n;
  let cov = 0;
  let vx = 0;
  let vy = 0;
  for (let i = 0; i < n; i++) {
    const dx = (xs[i] ?? 0) - mx;
    const dy = (ys[i] ?? 0) - my;
    cov += dx * dy;
    vx += dx * dx;
    vy += dy * dy;
  }
  if (vx === 0 || vy === 0) return 0;
  return r3(cov / Math.sqrt(vx * vy));
}

export interface StartOptions {
  readonly capacityFrames?: number;
  readonly label?: string;
  /** Phase times are CPU inside the callback; on a raster-bound scene that is a fraction of the frame. A fence that blocks until the queued draws have landed moves that cost into the phase that queued it, at the price of serialising the pipeline. */
  readonly fence?: (() => void) | null;
}

export class FrameProfiler {
  enabled = false;

  private capacity = 0;
  private write = 0;
  private written = 0;

  private phaseRing: Float64Array[] = [];
  private counterRing: Int32Array[] = [];
  private atRing = new Float64Array(0);
  private gapRing = new Float64Array(0);

  private readonly open = new Float64Array(PHASE_NAMES.length);
  private readonly acc = new Float64Array(PHASE_NAMES.length);
  private readonly counts = new Int32Array(COUNTER_NAMES.length);

  private frameAt = 0;
  private lastFrameAt = Number.NaN;
  private label = '';
  private fence: (() => void) | null = null;
  private stopTimer: ReturnType<typeof setTimeout> | null = null;

  start(options: StartOptions = {}): void {
    const requested = options.capacityFrames ?? DEFAULT_CAPACITY;
    const capacity = Math.max(1, Math.min(600000, Math.floor(requested)));
    if (capacity !== this.capacity) {
      this.capacity = capacity;
      this.phaseRing = PHASE_NAMES.map(() => new Float64Array(capacity));
      this.counterRing = COUNTER_NAMES.map(() => new Int32Array(capacity));
      this.atRing = new Float64Array(capacity);
      this.gapRing = new Float64Array(capacity);
    }
    this.write = 0;
    this.written = 0;
    this.lastFrameAt = Number.NaN;
    this.label = options.label ?? '';
    this.fence = options.fence ?? null;
    this.acc.fill(0);
    this.counts.fill(0);
    this.enabled = true;
  }

  stop(): void {
    this.enabled = false;
    if (this.stopTimer !== null) {
      clearTimeout(this.stopTimer);
      this.stopTimer = null;
    }
  }

  stopAfter(ms: number, then: () => void): void {
    if (this.stopTimer !== null) clearTimeout(this.stopTimer);
    this.stopTimer = setTimeout(() => {
      this.stopTimer = null;
      this.stop();
      then();
    }, ms);
  }

  get running(): boolean {
    return this.enabled;
  }

  get frameCount(): number {
    return this.written;
  }

  beginFrame(now: number): void {
    if (!this.enabled) return;
    this.frameAt = now;
    this.gapRing[this.write] = Number.isNaN(this.lastFrameAt) ? 0 : now - this.lastFrameAt;
    this.lastFrameAt = now;
    this.acc.fill(0);
    this.counts.fill(0);
    this.open[PHASE.frame] = performance.now();
  }

  begin(phase: number): void {
    if (!this.enabled) return;
    this.open[phase] = performance.now();
  }

  end(phase: number): void {
    if (!this.enabled) return;
    this.fence?.();
    this.acc[phase] = (this.acc[phase] ?? 0) + (performance.now() - (this.open[phase] ?? 0));
  }

  count(counter: number, value: number): void {
    if (!this.enabled) return;
    this.counts[counter] = value;
  }

  bump(counter: number): void {
    if (!this.enabled) return;
    this.counts[counter] = (this.counts[counter] ?? 0) + 1;
  }

  endFrame(): void {
    if (!this.enabled) return;
    this.end(PHASE.frame);
    const slot = this.write;
    for (let p = 0; p < PHASE_NAMES.length; p++) {
      const ring = this.phaseRing[p];
      if (ring !== undefined) ring[slot] = this.acc[p] ?? 0;
    }
    for (let c = 0; c < COUNTER_NAMES.length; c++) {
      const ring = this.counterRing[c];
      if (ring !== undefined) ring[slot] = this.counts[c] ?? 0;
    }
    this.atRing[slot] = this.frameAt;
    this.write = (slot + 1) % this.capacity;
    if (this.written < this.capacity) this.written += 1;
  }

  private ordered(source: Float64Array | Int32Array): Float64Array {
    const n = this.written;
    const out = new Float64Array(n);
    const first = this.write - n + this.capacity;
    for (let i = 0; i < n; i++) out[i] = source[(first + i) % this.capacity] ?? 0;
    return out;
  }

  snapshot(): ProfileSnapshot | null {
    if (this.written === 0) return null;

    const phaseValues = this.phaseRing.map((ring) => this.ordered(ring));
    const counterValues = this.counterRing.map((ring) => this.ordered(ring));
    const frameMsValues = phaseValues[PHASE.frame] ?? new Float64Array(0);
    const gapValues = this.ordered(this.gapRing);
    const atValues = this.ordered(this.atRing);
    const entityValues = counterValues[COUNTER.entities] ?? new Float64Array(0);

    const frameMs = summarise(frameMsValues);
    const gapMs = summarise(gapValues);

    const phases: Record<string, PhaseDistribution> = {};
    for (let p = 0; p < PHASE_NAMES.length; p++) {
      const name = PHASE_NAMES[p];
      if (name === undefined || name === 'frame') continue;
      const distribution = summarise(phaseValues[p] ?? new Float64Array(0));
      phases[name] = {
        ...distribution,
        shareOfFrame: frameMs.total === 0 ? 0 : r3(distribution.total / frameMs.total),
      };
    }

    const counts: Record<string, Distribution> = {};
    for (let c = 0; c < COUNTER_NAMES.length; c++) {
      const name = COUNTER_NAMES[c];
      if (name === undefined) continue;
      counts[name] = summarise(counterValues[c] ?? new Float64Array(0));
    }

    const spanMs = (atValues[atValues.length - 1] ?? 0) - (atValues[0] ?? 0);

    return {
      format: 'wis-render-profile/1',
      label: this.label,
      build: buildStamp(),
      page: pageInfo(),
      frames: this.written,
      capacity: this.capacity,
      wrapped: this.written === this.capacity,
      spanMs: r3(spanMs),
      fps: spanMs <= 0 ? 0 : r3(((this.written - 1) * 1000) / spanMs),
      frameMs,
      gapMs,
      jank: jankOf(gapValues, frameMsValues),
      phases,
      counts,
      entityCorrelation: correlation(entityValues, frameMsValues),
      byEntityCount: bucketByLoad(entityValues, frameMsValues),
      worstFrames: worstFrames(atValues, gapValues, phaseValues, counterValues),
    };
  }
}

function worstFrames(
  atValues: Float64Array,
  gapValues: Float64Array,
  phaseValues: readonly Float64Array[],
  counterValues: readonly Float64Array[],
): WorstFrame[] {
  const frameMsValues = phaseValues[PHASE.frame] ?? new Float64Array(0);
  const order = Array.from({ length: frameMsValues.length }, (_, i) => i).sort(
    (a, b) => (frameMsValues[b] ?? 0) - (frameMsValues[a] ?? 0),
  );
  const origin = atValues[0] ?? 0;
  return order.slice(0, WORST_FRAMES).map((i) => {
    const phaseMs: Partial<Record<PhaseName, number>> = {};
    for (let p = 0; p < PHASE_NAMES.length; p++) {
      const name = PHASE_NAMES[p];
      if (name === undefined || name === 'frame') continue;
      const value = phaseValues[p]?.[i] ?? 0;
      if (value > 0) phaseMs[name] = r3(value);
    }
    const frameCounts: Partial<Record<CounterName, number>> = {};
    for (let c = 0; c < COUNTER_NAMES.length; c++) {
      const name = COUNTER_NAMES[c];
      if (name === undefined) continue;
      frameCounts[name] = counterValues[c]?.[i] ?? 0;
    }
    return {
      atMs: r3((atValues[i] ?? 0) - origin),
      frameMs: r3(frameMsValues[i] ?? 0),
      gapMs: r3(gapValues[i] ?? 0),
      phaseMs,
      counts: frameCounts,
    };
  });
}

function jankOf(gapValues: Float64Array, frameMsValues: Float64Array): Record<string, number> {
  const sortedGaps = Float64Array.from(gapValues).sort();
  const baseline = percentile(sortedGaps, 0.5);
  let dropped = 0;
  let longest = 0;
  for (const gap of gapValues) {
    if (gap > longest) longest = gap;
    if (baseline > 0 && gap > baseline * 1.5) dropped += Math.round(gap / baseline) - 1;
  }
  let over17 = 0;
  let over33 = 0;
  let over50 = 0;
  let over100 = 0;
  for (const ms of frameMsValues) {
    if (ms > 16.7) over17 += 1;
    if (ms > 33) over33 += 1;
    if (ms > 50) over50 += 1;
    if (ms > 100) over100 += 1;
  }
  return {
    vsyncBaselineMs: r3(baseline),
    droppedFrames: dropped,
    longestGapMs: r3(longest),
    framesOver16_7ms: over17,
    framesOver33ms: over33,
    framesOver50ms: over50,
    framesOver100ms: over100,
  };
}

function bucketByLoad(entityValues: Float64Array, frameMsValues: Float64Array): LoadBucket[] {
  const buckets: LoadBucket[] = [];
  for (let b = 0; b < LOAD_BOUNDS.length; b++) {
    const from = LOAD_BOUNDS[b] ?? 0;
    const to = b + 1 < LOAD_BOUNDS.length ? (LOAD_BOUNDS[b + 1] ?? null) : null;
    const picked: number[] = [];
    for (let i = 0; i < entityValues.length; i++) {
      const entities = entityValues[i] ?? 0;
      if (entities < from) continue;
      if (to !== null && entities >= to) continue;
      picked.push(frameMsValues[i] ?? 0);
    }
    if (picked.length === 0) continue;
    const sorted = Float64Array.from(picked).sort();
    buckets.push({
      entitiesFrom: from,
      entitiesTo: to,
      frames: picked.length,
      frameMsP50: r3(percentile(sorted, 0.5)),
      frameMsP95: r3(percentile(sorted, 0.95)),
      frameMsMax: r3(sorted[sorted.length - 1] ?? 0),
    });
  }
  return buckets;
}

function buildStamp(): string {
  return CLIENT_BUILD;
}

function pageInfo(): Record<string, string | number> {
  const info: Record<string, string | number> = {};
  if (typeof navigator !== 'undefined') info.userAgent = navigator.userAgent;
  if (typeof window !== 'undefined') {
    info.devicePixelRatio = window.devicePixelRatio;
    info.innerWidth = window.innerWidth;
    info.innerHeight = window.innerHeight;
  }
  if (typeof location !== 'undefined') info.pathname = location.pathname;
  return info;
}

export const profiler = new FrameProfiler();

export interface ProfileHandle {
  readonly running: boolean;
  readonly frames: number;
  start: (options?: StartOptions) => string;
  stop: () => string;
  record: (seconds?: number) => string;
  json: () => ProfileSnapshot | null;
  text: () => string;
  dump: () => ProfileSnapshot | null;
  copy: () => Promise<string>;
}

function dump(): ProfileSnapshot | null {
  const snapshot = profiler.snapshot();
  if (snapshot === null) {
    console.info('wis profile: nothing recorded. __wisProfile.start() first.');
    return null;
  }
  console.info(
    `wis profile: ${String(snapshot.frames)} frames over ${String(Math.round(snapshot.spanMs))} ms, ` +
      `${String(snapshot.fps)} fps, frame p50 ${String(snapshot.frameMs.p50)} ms / p95 ${String(snapshot.frameMs.p95)} ms / max ${String(snapshot.frameMs.max)} ms`,
  );
  console.table(snapshot.phases);
  console.table(snapshot.counts);
  console.table(snapshot.byEntityCount);
  console.info('wis profile JSON follows; copy it whole, or call __wisProfile.copy().');
  console.info(JSON.stringify(snapshot));
  return snapshot;
}

const handle: ProfileHandle = {
  get running() {
    return profiler.running;
  },
  get frames() {
    return profiler.frameCount;
  },
  start: (options?: StartOptions) => {
    profiler.start(options ?? {});
    return 'wis profile: recording. __wisProfile.dump() when the lag has happened.';
  },
  stop: () => {
    profiler.stop();
    return `wis profile: stopped after ${String(profiler.frameCount)} frames.`;
  },
  record: (seconds = 30) => {
    profiler.start({ label: `record ${String(seconds)}s` });
    profiler.stopAfter(Math.max(1, seconds) * 1000, () => {
      dump();
    });
    return `wis profile: recording for ${String(seconds)} s, then dumping.`;
  },
  json: () => profiler.snapshot(),
  text: () => JSON.stringify(profiler.snapshot()),
  dump,
  copy: async () => {
    const snapshot = profiler.snapshot();
    if (snapshot === null) return 'wis profile: nothing recorded.';
    const text = JSON.stringify(snapshot);
    await navigator.clipboard.writeText(text);
    return `wis profile: ${String(text.length)} bytes copied.`;
  },
};

function readFlag(): string | null {
  try {
    const fromUrl = new URLSearchParams(location.search).get('profile');
    if (fromUrl !== null) return fromUrl;
  } catch {}
  try {
    return localStorage.getItem('wis.profile');
  } catch {}
  return null;
}

export function enabledByFlag(flag: string | null): flag is string {
  return flag !== null && flag !== '' && flag !== '0' && flag !== 'false';
}

function install(): void {
  if (typeof window === 'undefined') return;
  Object.defineProperty(window, '__wisProfile', { value: handle, configurable: true });

  const flag = readFlag();
  if (!enabledByFlag(flag)) return;
  const capacity = Number.parseInt(flag, 10);
  profiler.start({
    label: `auto (profile=${flag})`,
    ...(Number.isFinite(capacity) && capacity > 1 ? { capacityFrames: capacity } : {}),
  });
  console.info('wis profile: on. __wisProfile.dump() to read it, .stop() to end it.');
}

install();
